import { useState, useCallback, useEffect, useRef } from 'react';

// Tracks the current view + the direction the user last navigated.
// Pair with a wrapper whose `key` is the current view — that way React
// remounts the wrapper on each navigation and the CSS animation retriggers.
//
//   <div key={view} className={`view-anim-${direction}`}>{view content}</div>
//
// `direction` is "forward" (slide in from the end side) or "back" (slide in
// from the start side). The classes are direction-only, not LTR/RTL-baked —
// the CSS uses translateX with logical sign so RTL works naturally.
//
// History sync: every in-app navigation is mirrored into window.history, and a
// popstate listener drives the view from history. This makes the device/browser
// back button navigate *within* the app (e.g. hardware-back on a restaurant menu
// returns to Browse) instead of doing nothing or dropping out of the PWA — HSA
// beta feedback flagged that the built-in back button didn't work.
export function useViewTransition(initialView) {
  const [view, setView] = useState(initialView);
  const [direction, setDirection] = useState('forward');
  const initialRef = useRef(initialView);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Tag the initial entry so the first hardware-back has a view to read.
    window.history.replaceState({ __vt: initialRef.current }, '');
    const onPop = (e) => {
      setDirection('back');
      setView(e.state?.__vt ?? initialRef.current);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigateTo = useCallback((nextView, dir = 'forward') => {
    setDirection(dir);
    setView(nextView);
    if (typeof window !== 'undefined') {
      window.history.pushState({ __vt: nextView }, '');
    }
  }, []);

  return { view, navigateTo, direction };
}
